/**
 * Copyright (c) VVDN Technologies Pvt Ltd. All rights reserved.
 * Proprietary and confidential — custom algorithms.
 */

import { assignOptimalChannels } from './rfChannels'
import { computeFloorMetrics, optimizationGrid } from './rfFloorMetrics'
import { clampApPosition, clampToFloorBounds, isServiceableCeilingApSite, pointInFloorBounds } from './floorBounds'
import { pointInObstacleFootprint } from './obstacles'
import type { ApMountType, FloorGeometry } from './types'
import type { HeatmapCell } from './rfSimulation'
import type {
  FloorRecommendation,
  FloorRecommendationFix,
  FloorWall,
  PlacedAp,
} from './types'
import { DEFAULT_WALL_HEIGHT_M, DEFAULT_WALL_THICKNESS_M } from './walls'

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n))
}

function distanceM(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  floorWidthM: number,
  floorHeightM: number,
): number {
  const dx = ((x2 - x1) / 100) * floorWidthM
  const dy = ((y2 - y1) / 100) * floorHeightM
  return Math.hypot(dx, dy)
}

function separateApPosition(
  anchor: { x: number; y: number },
  other: { x: number; y: number },
  targetSepM: number,
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
): { x: number; y: number } {
  let dxM = ((other.x - anchor.x) / 100) * floorWidthM
  let dyM = ((other.y - anchor.y) / 100) * floorHeightM
  let distM = Math.hypot(dxM, dyM)
  if (distM < 0.5) {
    dxM = targetSepM
    dyM = 0
    distM = targetSepM
  }
  if (distM >= targetSepM) return { x: other.x, y: other.y }
  const scale = targetSepM / distM
  return clampToFloorBounds(
    anchor.x + ((dxM * scale) / floorWidthM) * 100,
    anchor.y + ((dyM * scale) / floorHeightM) * 100,
    geometry,
  )
}

export const MIN_GAP_AP_SEPARATION_M = 12

/** Walkable floor area where an AP can be mounted (excludes lift shafts, racks, corners, etc.). */
export function isServiceableApSite(
  x: number,
  y: number,
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
  mount: ApMountType = 'ceiling',
): boolean {
  return (
    pointInFloorBounds(x, y, geometry) &&
    !pointInObstacleFootprint(x, y, geometry.obstacles, floorWidthM, floorHeightM) &&
    isServiceableCeilingApSite(x, y, geometry, mount)
  )
}

export function weakZoneCentroid(
  grid: HeatmapCell[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): { x: number; y: number } | null {
  const weak = grid.filter(
    c =>
      c.rssi < -75 &&
      isServiceableApSite(c.x, c.y, geometry, floorWidthM, floorHeightM),
  )
  if (weak.length === 0) return null
  return {
    x: weak.reduce((s, c) => s + c.x, 0) / weak.length,
    y: weak.reduce((s, c) => s + c.y, 0) / weak.length,
  }
}

/** Best new AP site: weak coverage, at least minSepM from every existing AP. */
export function findGapApPosition(
  aps: PlacedAp[],
  grid: HeatmapCell[],
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
  minSepM = MIN_GAP_AP_SEPARATION_M,
): { x: number; y: number } | null {
  const weak = grid.filter(
    c =>
      c.rssi < -75 &&
      isServiceableApSite(c.x, c.y, geometry, floorWidthM, floorHeightM),
  )
  if (weak.length === 0) return null

  const nearestApM = (x: number, y: number) =>
    aps.length === 0
      ? Infinity
      : Math.min(...aps.map(ap => distanceM(ap.x, ap.y, x, y, floorWidthM, floorHeightM)))

  let best: { x: number; y: number; score: number } | null = null
  for (const cell of weak) {
    const sep = nearestApM(cell.x, cell.y)
    if (sep < minSepM) continue
    const score = (-cell.rssi) + sep * 4
    if (!best || score > best.score) best = { x: cell.x, y: cell.y, score }
  }
  if (best) {
    return clampApPosition(best.x, best.y, 'ceiling', geometry)
  }

  const centroid = weakZoneCentroid(grid, geometry, floorWidthM, floorHeightM)
  if (!centroid || aps.length === 0) {
    return centroid ? clampApPosition(centroid.x, centroid.y, 'ceiling', geometry) : null
  }

  let nearest = aps[0]
  let minDist = Infinity
  for (const ap of aps) {
    const d = distanceM(ap.x, ap.y, centroid.x, centroid.y, floorWidthM, floorHeightM)
    if (d < minDist) {
      minDist = d
      nearest = ap
    }
  }
  if (minDist >= minSepM) {
    return clampApPosition(centroid.x, centroid.y, 'ceiling', geometry)
  }

  const pushed = separateApPosition(nearest, centroid, minSepM, floorWidthM, floorHeightM, geometry)
  if (
    !isServiceableApSite(pushed.x, pushed.y, geometry, floorWidthM, floorHeightM) ||
    nearestApM(pushed.x, pushed.y) < minSepM
  ) {
    return null
  }
  return clampApPosition(pushed.x, pushed.y, 'ceiling', geometry)
}

export function syncApChannels(aps: PlacedAp[], geometry: FloorGeometry): PlacedAp[] {
  const map = geometry.apChannels ?? {}
  return aps.map(ap => ({
    ...ap,
    channel: ap.channel ?? map[ap.id],
  }))
}

export function createPerimeterWalls(idPrefix = 'wall'): FloorWall[] {
  const base = {
    material: 'drywall' as const,
    heightM: DEFAULT_WALL_HEIGHT_M,
    thicknessM: DEFAULT_WALL_THICKNESS_M,
  }
  return [
    { id: `${idPrefix}-n`, x1: 0, y1: 0, x2: 100, y2: 0, ...base },
    { id: `${idPrefix}-e`, x1: 100, y1: 0, x2: 100, y2: 100, ...base },
    { id: `${idPrefix}-s`, x1: 100, y1: 100, x2: 0, y2: 100, ...base },
    { id: `${idPrefix}-w`, x1: 0, y1: 100, x2: 0, y2: 0, ...base },
  ]
}

export function recommendationFixLabel(rec: FloorRecommendation): string {
  if (rec.fix?.moveApId && rec.action === 'adjust_power') {
    return rec.fix.txByApId ? 'Separate APs + lower TX' : 'Separate APs'
  }
  switch (rec.action) {
    case 'adjust_power':
      return 'Auto-fix interference'
    case 'relocate_ap':
      return 'Separate APs'
    case 'add_ap':
      return 'Add AP in gap'
    case 'draw_walls':
      return 'Add perimeter walls'
    case 'draw_obstacles':
      return 'Add server rack'
    default:
      return 'Apply fix'
  }
}

export const NO_AVAILABLE_AP_MESSAGE = 'Not enough APs. Please purchase one.'

const COVERAGE_TARGET_WEAK_PCT = 15
const MAX_COVERAGE_AP_ESTIMATE = 8
const MIN_WEAK_GAIN_PCT = 2.5

/** Simulate gap AP adds (virtual inventory) to estimate how many more APs the floor needs. */
export function estimateAdditionalApsNeeded(
  aps: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): number {
  if (aps.length === 0) return 1

  let sim = aps.map(a => ({ ...a }))
  let added = 0

  for (let i = 0; i < MAX_COVERAGE_AP_ESTIMATE; i++) {
    const before = computeFloorMetrics(sim, geometry, floorWidthM, floorHeightM)
    if (before.weakPct <= COVERAGE_TARGET_WEAK_PCT) break

    const grid = optimizationGrid(sim, geometry, floorWidthM, floorHeightM)
    const pos = findGapApPosition(sim, grid, floorWidthM, floorHeightM, geometry)
    if (!pos) break

    const virtualId = `estimate-gap-${i}`
    sim = [
      ...sim,
      { id: virtualId, ap: virtualId, x: pos.x, y: pos.y, txPowerDbm: 15 },
    ]
    const channels = assignOptimalChannels(sim, geometry.bandGHz, floorWidthM, floorHeightM)
    sim = sim.map(ap => ({ ...ap, channel: channels[ap.id] ?? ap.channel }))

    const after = computeFloorMetrics(sim, geometry, floorWidthM, floorHeightM)
    const weakGain = before.weakPct - after.weakPct
    added++
    if (after.weakPct <= COVERAGE_TARGET_WEAK_PCT || weakGain < MIN_WEAK_GAIN_PCT) break
  }

  if (added > 0) return added

  const metrics = computeFloorMetrics(aps, geometry, floorWidthM, floorHeightM)
  if (metrics.weakPct <= COVERAGE_TARGET_WEAK_PCT) return 0
  return Math.max(1, Math.min(MAX_COVERAGE_AP_ESTIMATE, Math.ceil(metrics.weakPct / 25)))
}

export type NoAvailableApDialogContent = {
  title: string
  message: string
  details: string[]
  /** Additional APs RF planning suggests for this floor (beyond current placement). */
  estimatedApsNeeded: number
  /** APs to purchase given current spare inventory. */
  apShortfall: number
  spareCount: number
}

export function noAvailableApDialogContent(
  inventoryAps: { id: string; name: string }[],
  placed: PlacedAp[],
  geometry: FloorGeometry,
  floorWidthM: number,
  floorHeightM: number,
): NoAvailableApDialogContent {
  const onFloor = new Set(placed.map(a => a.ap))
  const spareCount = inventoryAps.filter(ap => !onFloor.has(ap.name)).length
  const inventoryCount = inventoryAps.length
  const placedCount = placed.length
  const estimatedApsNeeded = Math.max(1, estimateAdditionalApsNeeded(placed, geometry, floorWidthM, floorHeightM))
  const apShortfall = Math.max(1, estimatedApsNeeded - spareCount)

  const details: string[] = []
  details.push(
    `RF analysis estimates ${estimatedApsNeeded} additional AP${estimatedApsNeeded !== 1 ? 's' : ''} on this floor to bring weak coverage below ${COVERAGE_TARGET_WEAK_PCT}%.`,
  )
  if (inventoryCount === 0) {
    details.push('Your inventory has no access points yet.')
    details.push(`Purchase at least ${apShortfall} wireless AP${apShortfall !== 1 ? 's' : ''} to begin filling coverage gaps.`)
  } else {
    details.push(
      `${placedCount} AP${placedCount !== 1 ? 's' : ''} on this floor · ${inventoryCount} in inventory · ${spareCount} spare.`,
    )
    details.push('All licensed APs are already deployed — none are available for a new placement.')
    details.push(
      spareCount > 0
        ? `Assign ${apShortfall} more AP${apShortfall !== 1 ? 's' : ''} to inventory (purchase or reallocate from other floors).`
        : `Purchase at least ${apShortfall} more AP${apShortfall !== 1 ? 's' : ''}, or remove APs from another floor to free inventory.`,
    )
  }
  details.push('When spare APs are available, click Add AP in gap to place at the suggested weak zones.')

  return {
    title: 'Not enough APs',
    message: `Please purchase at least ${apShortfall} more AP${apShortfall !== 1 ? 's' : ''}.`,
    details,
    estimatedApsNeeded,
    apShortfall,
    spareCount,
  }
}

export function pickInventoryApName(
  inventoryAps: { id: string; name: string }[],
  placed: PlacedAp[],
): string | null {
  const onFloor = new Set(placed.map(a => a.ap))
  return inventoryAps.find(ap => !onFloor.has(ap.name))?.name ?? null
}

export function hasAvailableInventoryAp(
  inventoryAps: { id: string; name: string }[],
  placed: PlacedAp[],
): boolean {
  return pickInventoryApName(inventoryAps, placed) != null
}

export function interferencePowerFix(aps: PlacedAp[], apIds: string[]): FloorRecommendationFix {
  const ids = new Set(apIds)
  const txByApId: Record<string, number> = {}
  const reducibleIds: string[] = []
  for (const ap of aps) {
    if (!ids.has(ap.id)) continue
    const current = ap.txPowerDbm ?? 15
    const drop = current >= 20 ? 5 : current >= 17 ? 4 : 3
    const next = Math.max(10, current - drop)
    if (next < current) {
      txByApId[ap.id] = next
      reducibleIds.push(ap.id)
    }
  }
  if (reducibleIds.length === 0) return {}
  return { apIds: reducibleIds, txByApId }
}

export function combinedInterferenceFix(
  aps: PlacedAp[],
  pair: { a: PlacedAp; b: PlacedAp; distM: number },
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
): FloorRecommendationFix {
  const atMinPower = [pair.a, pair.b].every(ap => (ap.txPowerDbm ?? 15) <= 10)
  const targetSepM = atMinPower ? 18 : pair.distM < 8 ? 16 : MIN_GAP_AP_SEPARATION_M
  return {
    ...relocatePairFix(pair.a, pair.b, floorWidthM, floorHeightM, geometry, targetSepM),
    ...interferencePowerFix(aps, [pair.a.id, pair.b.id]),
  }
}

export function relocatePairFix(
  a: PlacedAp,
  b: PlacedAp,
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
  targetSepM = MIN_GAP_AP_SEPARATION_M,
): FloorRecommendationFix {
  const moveTo = separateApPosition(a, b, targetSepM, floorWidthM, floorHeightM, geometry)
  if (Math.hypot(b.x - moveTo.x, b.y - moveTo.y) <= 0.5) return {}
  return { moveApId: b.id, moveTo }
}

export function hasAutoFix(rec: FloorRecommendation): boolean {
  const fix = rec.fix
  if (!fix) return false
  return !!(
    fix.moveApId
    || fix.newApAt
    || fix.perimeterWalls
    || fix.obstacleType
    || (fix.apIds?.length && fix.txByApId && Object.keys(fix.txByApId).length > 0)
  )
}

/** Assign channels and return updated AP list + channel map. */
export function applyChannelPlan(
  aps: PlacedAp[],
  bandGHz: number,
  floorWidthM: number,
  floorHeightM: number,
): { aps: PlacedAp[]; channels: Record<string, number> } {
  const channels = assignOptimalChannels(aps, bandGHz, floorWidthM, floorHeightM)
  return {
    aps: aps.map(ap => ({ ...ap, channel: channels[ap.id] ?? ap.channel })),
    channels,
  }
}

export function describeFix(
  rec: FloorRecommendation,
  aps: PlacedAp[],
  bandGHz: number,
): string {
  const fix = rec.fix
  if (!fix) return 'Review manually on the map'
  const parts: string[] = []
  if (fix.moveApId && fix.moveTo) {
    const ap = aps.find(a => a.id === fix.moveApId)
    parts.push(`Move ${ap?.ap ?? 'AP'} to reduce overlap`)
  }
  if (fix.txByApId && Object.keys(fix.txByApId).length > 0) {
    const names = Object.keys(fix.txByApId)
      .map(id => aps.find(a => a.id === id)?.ap ?? id)
      .slice(0, 2)
    parts.push(`Lower TX on ${names.join(', ')}`)
  }
  if (fix.newApAt) parts.push('Place new AP in weak zone (≥12 m from others, not inside obstacles)')
  if (fix.perimeterWalls) parts.push('Add perimeter walls')
  if (fix.obstacleType) parts.push('Add server rack obstacle')
  if (parts.length === 0 && hasAutoFix(rec)) parts.push('Apply RF adjustment')
  if (parts.length === 0) return 'Run Optimize floor plan for channel + placement plan'
  return parts.join(' · ')
}
